import React, { useState, useEffect } from 'react';
import { postApi } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Support = () => {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('General Inquiry');
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);

  const faqs = [
    { q: 'How to deposit funds?', a: 'Navigate to the Deposit page, copy the USDT BEP-20 address, and send from your wallet. Deposits confirm within 1-5 minutes.' },
    { q: 'Minimum withdrawal amount?', a: 'The minimum withdrawal amount is $10 USDT. A $2.50 network fee applies to all withdrawals.' },
    { q: 'How are staking rewards calculated?', a: 'Daily rewards are calculated based on your staked amount and the selected plan ROI percentage, credited every 24 hours.' },
    { q: 'How does the referral system work?', a: 'Share your referral code and earn direct bonuses when your referrals stake. Bonuses extend through multiple network levels.' },
  ];

  const [openFaq, setOpenFaq] = useState(0);

  const fetchTickets = async () => {
    try {
      const formData = new FormData();
      formData.append('ticket_id', '');
      const res = await postApi('support-tickets', formData);
      if (res.data?.status) {
        setTickets(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching tickets', err);
    } finally {
      setLoadingTickets(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) return;
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('subject', `[${category}] ${subject}`);
      formData.append('message', message);
      if (attachment) {
        formData.append('attachment', attachment);
      }

      const res = await postApi('support-tickets/create', formData);
      if (res.data?.status) {
        toast.success(res.data?.message || 'Ticket created successfully');
        setSubmitted(true);
        setSubject('');
        setMessage('');
        setAttachment(null);
        fetchTickets();
      } else {
        toast.error(res.data?.message || 'Failed to create ticket');
      }
    } catch (err) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const openTicketDetails = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
  };

  return (
    <div className="page-wrapper">
      <ToastContainer position="top-right" autoClose={4000} theme="dark" />

      {/* Header */}
      <div className="page-header mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <div className="section-label">Help Center</div>
          <h2 className="page-title">Support</h2>
          <p className="page-subtitle">We're here to help — 24/7 dedicated support</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="dot-live"></span>
          <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>Support Online</span>
        </div>
      </div>

      {/* Quick Contact Cards */}
      <div className="row g-3 mb-4">
        {[
          { icon: 'bi-envelope', label: 'Email Support', value: 'support@antipsun.com', color: 'green' },
          { icon: 'bi-telegram', label: 'Telegram', value: '@antipsun_support', color: 'blue' },
          { icon: 'bi-clock', label: 'Response Time', value: 'Within 2 hours', color: 'yellow' },
        ].map((c, i) => (
          <div key={i} className="col-md-4">
            <div className={`metric-card ${c.color}`}>
              <div className={`metric-icon ${c.color}`}><i className={`bi ${c.icon}`}></i></div>
              <div>
                <div className="metric-label">{c.label}</div>
                <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'white', marginTop: 2 }}>{c.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row g-4">
        {/* Ticket Form */}
        <div className="col-lg-7">
          <div className="p-card mb-4">
            <p className="section-label">New Request</p>
            <h5 style={{ fontWeight: 700, marginBottom: '1.5rem' }}>Create Support Ticket</h5>

            {submitted ? (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                <h5 style={{ fontWeight: 700, color: 'var(--page-accent)' }}>Ticket Submitted!</h5>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                  Our team will respond within 2 hours via email.
                </p>
                <button className="btn btn-gradient" onClick={() => setSubmitted(false)} style={{ borderRadius: 12, fontWeight: 700 }}>
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row g-3 mb-3">
                  <div className="col-md-8">
                    <label className="p-label">Subject</label>
                    <input type="text" className="p-input form-control" placeholder="Describe your issue briefly..."
                      value={subject} onChange={e => setSubject(e.target.value)} required />
                  </div>
                  <div className="col-md-4">
                    <label className="p-label">Category</label>
                    <select className="p-input form-select" value={category} onChange={e => setCategory(e.target.value)} style={{ appearance: 'auto' }}>
                      {['General Inquiry', 'Technical Issue', 'Account Problem', 'Payment Issue', 'Staking Query', 'Other'].map(opt => (
                        <option key={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="p-label">Message</label>
                  <textarea
                    className="p-input form-control"
                    rows="5"
                    placeholder="Please describe your issue in detail. Include any error messages, transaction IDs, or relevant information..."
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    required
                    style={{ resize: 'vertical' }}
                  ></textarea>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>
                    {message.length}/500 characters
                  </div>
                </div>

                <div className="mb-4">
                  <label className="p-label">Attachment (Optional)</label>
                  <input
                    type="file"
                    className="p-input form-control"
                    onChange={e => setAttachment(e.target.files[0])}
                    accept="image/*"
                    style={{ padding: '0.6rem 1rem' }}
                  />
                </div>

                <button type="submit" className="btn btn-gradient" disabled={submitting || !subject || !message}
                  style={{ borderRadius: 12, padding: '0.75rem 2rem', fontWeight: 700 }}>
                  {submitting
                    ? <><span className="spinner-border spinner-border-sm me-2"></span>Submitting...</>
                    : <><i className="bi bi-send me-2"></i>Submit Ticket</>
                  }
                </button>
              </form>
            )}
          </div>

          {/* Tickets History */}
          <div className="p-card">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <p className="section-label mb-0">Tickets</p>
                <h5 style={{ margin: 0, fontWeight: 700 }}>Recent Tickets</h5>
              </div>
              <span style={{ fontSize: '0.78rem', background: 'rgba(255,255,255,0.05)', padding: '4px 10px', borderRadius: 20, color: 'var(--text-secondary)' }}>
                {tickets.length} tickets
              </span>
            </div>
            <div className="p-table-wrap">
              {loadingTickets ? (
                <div className="text-center py-4"><div className="spinner-border text-success"></div></div>
              ) : tickets.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon"><i className="bi bi-ticket-detailed"></i></div>
                  <h6>No Tickets Yet</h6>
                  <p>Your support tickets will appear here</p>
                </div>
              ) : (
                <table className="p-table">
                  <thead>
                    <tr><th>Ticket</th><th>Subject</th><th>Date</th><th>Status</th><th></th></tr>
                  </thead>
                  <tbody>
                    {tickets.map(t => (
                      <tr key={t.id}>
                        <td className="mono dim" style={{ fontSize: '0.8rem' }}>#{t.id}</td>
                        <td style={{ fontWeight: 600, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.subject}
                        </td>
                        <td className="dim" style={{ fontSize: '0.8rem' }}>{t.date_time?.split(' ')[0] || t.date_time}</td>
                        <td>
                          <span className={`s-pill ${t.status === 'success' ? 'success' : t.status === 'review' ? 'review' : 'pending'}`}>
                            {t.status === 'review' ? 'In Review' : t.status === 'success' ? 'Resolved' : 'Pending'}
                          </span>
                        </td>
                        <td>
                          <button
                            className="btn btn-sm"
                            onClick={() => openTicketDetails(t)}
                            style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: '0.78rem' }}
                            title="View Details"
                          >
                            <i className="bi bi-eye"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Right — FAQ */}
        <div className="col-lg-5">
          <div className="p-card mb-4">
            <p className="section-label">Self-Service</p>
            <h5 style={{ fontWeight: 700, marginBottom: '1.25rem' }}>FAQ</h5>
            <div className="d-flex flex-column gap-2">
              {faqs.map((faq, i) => (
                <div key={i} style={{
                  background: 'rgba(255,255,255,0.02)',
                  borderRadius: 14,
                  border: `1px solid ${openFaq === i ? 'rgba(0,255,149,0.3)' : 'rgba(255,255,255,0.06)'}`,
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                }}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    style={{ width: '100%', padding: '1rem 1.2rem', background: 'none', border: 'none', color: 'white', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '0.9rem' }}>
                    {faq.q}
                    <i className={`bi ${openFaq === i ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: 'var(--text-secondary)', flexShrink: 0, fontSize: '0.9rem' }}></i>
                  </button>
                  {openFaq === i && (
                    <div style={{ padding: '0 1.2rem 1rem', fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Live Chat CTA */}
          <div className="p-card" style={{ background: 'linear-gradient(135deg, rgba(0,255,149,0.08), rgba(0,212,255,0.05))', borderColor: 'rgba(0,255,149,0.25)' }}>
            <div className="text-center">
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>💬</div>
              <h5 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Need Immediate Help?</h5>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginBottom: '1.25rem' }}>
                Join our Telegram community for faster support and updates.
              </p>
              <a href="https://t.me/antipsun_support" target="_blank" rel="noreferrer"
                className="btn btn-gradient w-100" style={{ borderRadius: 12, fontWeight: 700, textDecoration: 'none' }}>
                <i className="bi bi-telegram me-2"></i>Open Telegram
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Ticket Details Modal ── */}
      {showTicketModal && selectedTicket && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ background: '#0a1628', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 24 }}>
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="fw-700 mb-0" style={{ color: 'white' }}>
                  <i className="bi bi-ticket-detailed me-2" style={{ color: 'var(--page-accent)' }}></i>Ticket Details #{selectedTicket.id}
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setShowTicketModal(false)}></button>
              </div>
              <div className="modal-body px-4 py-3">
                <div className="mb-3">
                  <h6 style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Subject</h6>
                  <div style={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}>{selectedTicket.subject}</div>
                </div>
                <div className="mb-3">
                  <h6 style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Message / Description</h6>
                  <div style={{ background: 'rgba(255,255,255,0.03)', padding: '1rem', borderRadius: 12, color: 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
                    {selectedTicket.description || 'No description provided.'}
                  </div>
                </div>
                {selectedTicket.image && (
                  <div className="mb-3">
                    <h6 style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Attachment</h6>
                    <div style={{ background: 'rgba(255,255,255,0.03)', padding: '0.5rem', borderRadius: 12, display: 'inline-block' }}>
                      <a href={selectedTicket.image} target="_blank" rel="noreferrer">
                        <img src={selectedTicket.image} alt="Ticket Attachment" style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: 8 }} />
                      </a>
                    </div>
                  </div>
                )}
                <div className="mb-3">
                  <h6 style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>Support Reply</h6>
                  <div style={{
                    background: selectedTicket.answer ? 'rgba(0,255,149,0.05)' : 'rgba(255,255,255,0.03)',
                    border: selectedTicket.answer ? '1px solid rgba(0,255,149,0.2)' : '1px dashed rgba(255,255,255,0.1)',
                    padding: '1rem',
                    borderRadius: 12,
                    color: selectedTicket.answer ? 'white' : 'var(--text-muted)',
                    whiteSpace: 'pre-wrap'
                  }}>
                    {selectedTicket.answer || 'No reply yet. Our team is reviewing your ticket.'}
                  </div>
                </div>
                <div className="d-flex justify-content-between mt-4">
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Status: <strong style={{ color: selectedTicket.status === 'success' ? '#22c55e' : selectedTicket.status === 'review' ? '#f59e0b' : '#3b82f6', textTransform: 'capitalize' }}>{selectedTicket.status}</strong>
                  </span>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                    Created: {selectedTicket.date_time}
                  </span>
                </div>
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button
                  className="btn w-100"
                  onClick={() => setShowTicketModal(false)}
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '0.75rem' }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Support;